import { useEffect, useMemo, useState } from 'react'
import { useDebounce } from '@node-space/hooks'
import { type RowsPerPageType } from '@node-space/ui-components'
import { SEARCH_INPUT_DEBOUNCE_TIME } from 'constants/General'
import { useCurrencyFilterOptions } from 'hooks/queries/Customers/useCurrencyFilterOptions'
import { useGetCustomersWalletsQuery } from 'hooks/queries/Customers/useGetCustomersWallets'
import { usePaginationPagingPersist } from 'hooks/usePaginationPagingPersist'
import type { CustomerWalletFilterProps } from 'pages/Customers/CustomerView/CustomerWallets'
import {
  CustomerWalletStatusType,
  type CustomerWallet,
  type CustomerWalletStatus,
} from 'types/customers'

const EMPTY_WALLETS: CustomerWallet[] = []

export const useCustomerWalletFilters = (customerReference: string) => {
  const [walletNameSearch, setWalletNameSearch] = useState<string>('')
  const debouncedWalletNameSearch = useDebounce(walletNameSearch, SEARCH_INPUT_DEBOUNCE_TIME)
  const [currencyFilter, setCurrencyFilter] = useState<string>('')
  const [statusFilterValues, setStatusFilterValues] = useState<CustomerWalletStatus[]>([
    CustomerWalletStatusType.ACTIVE,
  ])

  const [page, setPage] = useState<number>(0)
  const [rowsPerPage, setRowsPerPage] = useState<RowsPerPageType>(10)

  const { currencyOptions, isFetchingCurrencies } = useCurrencyFilterOptions(customerReference)

  const {
    data: walletData,
    isFetching: isFetchingCustomerWallets,
    isError: isErrorFetchingCustomerWallets,
    refetch: fetchCustomerWallets,
  } = useGetCustomersWalletsQuery({
    customerReference,
    page,
    size: rowsPerPage,
    ...(currencyFilter && { currencyCodeSearch: currencyFilter }),
    ...(debouncedWalletNameSearch && { searchTerm: debouncedWalletNameSearch }),
    ...(!!statusFilterValues.length && { statuses: statusFilterValues }),
  })

  const customerWallets = walletData?.content ?? EMPTY_WALLETS
  const customerWalletPagination = walletData?.pagination

  const { paging, showPagination } = usePaginationPagingPersist(customerWalletPagination)

  const handlePaginationChange = (newPage: number) => setPage(newPage)

  const handleRowsPerPageChange = (newRowsPerPage: RowsPerPageType) => {
    setRowsPerPage(newRowsPerPage)
    setPage(0)
  }

  useEffect(() => {
    setPage(0)
  }, [debouncedWalletNameSearch, currencyFilter, statusFilterValues])

  const filterProps: CustomerWalletFilterProps = useMemo(
    () => ({
      walletNameSearch,
      isSearching: !!debouncedWalletNameSearch,
      onChangeWalletNameSearch: setWalletNameSearch,
      currencyFilter,
      onCurrencyFilterChange: setCurrencyFilter,
      currencyOptions,
      isFetchingCurrencies,
      statusFilterValues,
      onStatusFilterChange: setStatusFilterValues,
    }),
    [
      walletNameSearch,
      debouncedWalletNameSearch,
      currencyFilter,
      currencyOptions,
      isFetchingCurrencies,
      statusFilterValues,
    ]
  )

  const isPaginationVisible = !isErrorFetchingCustomerWallets && !!paging && !showPagination

  return {
    filterProps,
    customerWallets,
    isFetchingCustomerWallets,
    isErrorFetchingCustomerWallets,
    fetchCustomerWallets,
    pagination: {
      page,
      rowsPerPage,
      paging,
      isVisible: isPaginationVisible,
      onChangePage: handlePaginationChange,
      onChangeRowsPerPage: handleRowsPerPageChange,
    },
  }
}
